import { z } from 'zod';
import { Message } from './chat.js';
import { PersonRef } from './people.js';
import { Role } from './roles.js';
/**
 * Report and block (docs/adr/0039-report-and-block.md). Reports go to the workspace's owners and admins,
 * never to Chapsi staff. Blocks are private to the person who blocks.
 */
export const ReportReason = z.enum(['spam', 'harassment', 'inappropriate', 'other']);
export const REPORT_REASONS = {
    spam: { label: 'Spam', hint: 'Advertising, scams or the same thing over and over' },
    harassment: { label: 'Harassment or bullying', hint: 'Abuse, threats or targeting someone' },
    inappropriate: { label: 'Something inappropriate', hint: 'Offensive, explicit or hateful content' },
    other: { label: 'Something else', hint: 'Tell the workspace admins what is wrong' },
};
export const REPORT_NOTE_MAX = 500;
/** Reports one person can send in an hour. */
export const REPORTS_PER_HOUR = 10;
export const ReportMessageInput = z.object({
    reason: ReportReason,
    note: z.string().trim().max(REPORT_NOTE_MAX).default(''),
});
export const ReportStatus = z.enum(['open', 'dismissed', 'actioned']);
export const ReportAction = z.enum(['dismiss', 'delete_message', 'remove_from_channel', 'remove_from_workspace']);
export const REPORT_ACTIONS = {
    dismiss: { label: 'Dismiss', done: 'Dismissed' },
    delete_message: { label: 'Delete message', done: 'Message deleted' },
    remove_from_channel: { label: 'Remove from channel', done: 'Removed from the channel' },
    remove_from_workspace: { label: 'Remove from workspace', done: 'Removed from the workspace' },
};
/** What the reporter is told: never who acted or what they did. */
export const ReportOutcome = z.enum(['actioned', 'dismissed']);
export const reportOutcomeText = (o) => (o === 'actioned' ? 'Action was taken.' : 'No action was needed.');
export const ReportedChannel = z.object({
    id: z.uuid(),
    /** Null for a DM. */
    name: z.string().nullable(),
    kind: z.enum(['client', 'project', 'team', 'dm']),
    /** A private channel or DM the reviewer is not in: they see the reported message only. */
    private: z.boolean(),
});
export const MessageReport = z.object({
    id: z.uuid(),
    messageId: z.uuid(),
    reason: ReportReason,
    note: z.string(),
    status: ReportStatus,
    action: ReportAction.nullable(),
    reporter: PersonRef.nullable(),
    author: PersonRef.nullable(),
    authorRole: Role.nullable(),
    channel: ReportedChannel,
    /** Other people who reported the same message (open reports included). */
    otherReports: z.number().int().min(0),
    /** One line of the live message, mentions as names. Empty once it is deleted. */
    excerpt: z.string(),
    messageDeleted: z.boolean(),
    /** The author changed the message after it was reported. */
    editedAfterReport: z.boolean(),
    /** The actions this reviewer may take now (empty once the report is closed). */
    actions: z.array(ReportAction),
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
    resolvedBy: PersonRef.nullable(),
});
export const ReportQuery = z.object({ status: z.enum(['open', 'closed']).default('open') });
export const ReportList = z.object({ items: z.array(MessageReport), openCount: z.number().int().min(0) });
export const ReportDetail = z.object({
    report: MessageReport,
    /** The reported message itself (null once deleted). */
    message: Message.nullable(),
    /** Up to two messages either side, only when the reviewer can read the channel anyway. */
    before: z.array(Message),
    after: z.array(Message),
});
export const ResolveReportInput = z.object({ action: ReportAction });
export const BlockedPerson = z.object({ user: PersonRef, createdAt: z.string() });
export const BlockList = z.object({ items: z.array(BlockedPerson) });
/**
 * Collapse rule shared by web and mobile: a message from someone you blocked is folded behind "Show",
 * unless you opened it. Your own messages and system lines never are.
 */
export function isFromBlocked(message, blocked) {
    return message.kind === 'user' && !!message.author && blocked.has(message.author.id);
}
/** Which review actions apply to a report, before permissions: removal is for guests, channel removal never for DMs. */
export function applicableReportActions(r) {
    if (r.status !== 'open')
        return [];
    const out = ['dismiss'];
    if (!r.messageDeleted)
        out.push('delete_message');
    if (r.authorRole === 'guest') {
        if (r.channelKind !== 'dm')
            out.push('remove_from_channel');
        out.push('remove_from_workspace');
    }
    return out;
}
/** The message a sender sees when a DM can't be delivered (a block, or the other person left). */
export const NOT_DELIVERED = 'Your message could not be delivered.';
//# sourceMappingURL=safety.js.map